import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Bell, Users, User, Save, Info } from "lucide-react";
import { toast } from "sonner";

const CLIENT_LABELS = {
  status_change: { title: "Status change", desc: "When you update the client's status (Started → Waiting on Trade Line, etc.)" },
  schedule_options_sent: { title: "Processing times sent", desc: "When admin sends the client 3-5 time options to pick from" },
  schedule_confirmed: { title: "Time confirmed", desc: "When admin schedules a client directly (bypassing the pick list)" },
  schedule_started: { title: "Work started", desc: "When someone hits 'Start Now' on their scheduled work" },
  schedule_completed: { title: "Work completed", desc: "When someone hits 'Complete' on a client's file" },
  meeting_approved: { title: "Meeting confirmed/rescheduled", desc: "When admin approves or reschedules the client's meeting" },
};

const TEAM_LABELS = {
  new_message_from_client: { title: "Client sent a message", desc: "Portal chat message from the client to the team" },
  new_document_from_client: { title: "Client uploaded a document", desc: "New file dropped into their file by the client" },
  new_meeting_booked: { title: "Client booked a meeting", desc: "Someone reserved a slot in Meetings" },
  schedule_confirmed_by_client: { title: "Client picked a processing time", desc: "Client confirmed one of your proposed times" },
  new_client_signup: { title: "New client signup", desc: "Someone just registered on Client Quack" },
  meeting_approved: { title: "Meeting confirmed", desc: "Broadcasts confirmation to the whole team (excluding the person who confirmed)" },
};

export default function SmsAutomationsTab() {
  const [state, setState] = useState({ client: {}, team: {} });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/close-sms/automations");
      setState(data);
    } catch (e) {
      toast.error("Couldn't load SMS automation settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (section, key) => {
    setState((s) => ({ ...s, [section]: { ...s[section], [key]: !s[section]?.[key] } }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/close-sms/automations", state);
      setState(data);
      setDirty(false);
      toast.success("SMS automations saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  const allOn = (section, on) => {
    setState((s) => ({ ...s, [section]: Object.fromEntries(Object.keys(s[section] || {}).map((k) => [k, on])) }));
    setDirty(true);
  };

  if (loading) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="sms-automations-tab">
      <Card className="p-4 rounded-2xl border-emerald-100 bg-emerald-50/60 flex gap-2 text-sm text-emerald-900" data-testid="sms-automations-info">
        <Info size={16} className="mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold">All SMS routes through your Close account (+17572908714).</p>
          <p className="text-xs mt-1">Team alerts are sent to every active staff user's cell phone (from user profile). Client alerts are attributed to their Close lead so they thread with the rest of the SMS history.</p>
        </div>
      </Card>

      {/* Client alerts */}
      <Card className="p-5 rounded-2xl border-slate-100" data-testid="client-automations-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><User size={18} /></div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg">Text the client when…</h3>
            <p className="text-xs text-slate-500">Alerts sent to the client's phone. Includes their name and a link to their portal.</p>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => allOn("client", true)} className="text-xs" data-testid="client-all-on">All on</Button>
            <Button size="sm" variant="ghost" onClick={() => allOn("client", false)} className="text-xs" data-testid="client-all-off">All off</Button>
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(CLIENT_LABELS).map(([k, meta]) => (
            <div key={k} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`client-toggle-${k}`}>
              <Switch checked={!!state.client?.[k]} onCheckedChange={() => toggle("client", k)} className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900">{meta.title}</p>
                <p className="text-xs text-slate-500">{meta.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Team alerts */}
      <Card className="p-5 rounded-2xl border-slate-100" data-testid="team-automations-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Users size={18} /></div>
          <div className="flex-1">
            <h3 className="font-display font-bold text-lg">Text the team when…</h3>
            <p className="text-xs text-slate-500">Alerts sent to every active staff member's cell phone from their profile.</p>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => allOn("team", true)} className="text-xs" data-testid="team-all-on">All on</Button>
            <Button size="sm" variant="ghost" onClick={() => allOn("team", false)} className="text-xs" data-testid="team-all-off">All off</Button>
          </div>
        </div>
        <div className="space-y-2">
          {Object.entries(TEAM_LABELS).map(([k, meta]) => (
            <div key={k} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`team-toggle-${k}`}>
              <Switch checked={!!state.team?.[k]} onCheckedChange={() => toggle("team", k)} className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900">{meta.title}</p>
                <p className="text-xs text-slate-500">{meta.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-center justify-end gap-2 sticky bottom-4 bg-slate-50/80 backdrop-blur border border-slate-100 rounded-2xl p-3 shadow-sm">
        {dirty && <p className="text-xs text-amber-700 font-semibold mr-auto">Unsaved changes</p>}
        <Button onClick={save} disabled={busy || !dirty} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sms-automations-save">
          <Save size={14} className="mr-1" /> {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
